import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { MaterialReactTable, type MRT_ColumnDef, type MRT_Virtualizer, type MRT_FilterFn } from 'material-react-table'
import {
  QueryClient,
  QueryClientProvider,
  useInfiniteQuery,
  useQuery,
  focusManager,
  useQueryClient,
  InfiniteData,
} from '@tanstack/react-query'
import {
  DivisionId,
  GoalId,
  GoalWithProgress,
  GroupView,
  UserId,
  CompanyId,
  Role,
  ProgressType,
  UserDailyDiaryProgress,
  UserCustomDailyProgress,
  UserDailyProgress,
} from '../../services/api.types'
import apiUrl from '../../services/apiUrl'
import ApiClient from '../../services/ApiClient'
import { ActivityView } from '../../views/Activity/store/ActivityReducer'
import { t } from 'ttag'
import BlueTooltip from '../YumBlueTooltip/YumBlueTooltip'
import { countryToFlag, countryWithFlag, getCountryName } from '../YumCountrySelect/YumCountrySelect'
import { formatThousands } from '../../common/helpers/translations'
import Loader from '../Loader/Loader'

import { ReactComponent as IconGoalLine } from '../../assets/v2/icons/icon-goal-line.svg'
import { ReactComponent as IconGoalLineInverse } from '../../assets/v2/icons/icon-goal-line-inverse.svg'
import { ProgressCell } from '../Table/components/ProgressCell/ProgressCell'
import {
  isActivityIndexType,
  isCollectiveType,
  isForceCollectiveTotal,
  getGoalTypeFromActivityView,
} from '../../common/helpers/challenges'
import { GoalConfig, GoalsConfig } from '../../views/ChallengeAdmin/ChallengeAdmin'
import { borderGray, darkGreen, lightestBlue } from '../../styles/material-ui-theme-v2'
import { Box } from '@material-ui/core'
import { PluginListenerHandle } from '@capacitor/core'
import { App } from '@capacitor/app'
import YumLogActivity from '../YumLogActivity/YumLogActivity'
import { isRtl } from '../../i18nInit'
import LeaderboardMembersModal from './LeaderboardMembersModal'

type GetPeopleLeaderboardProgress$Response = {
  data: Array<LeaderboardProgress>
  totalRowCount: number
  ofPeriodPassedPercentage: number
}

type LeaderboardProgress = {
  userId: UserId
  rank: number
  name: string
  teamId: DivisionId
  teamName: string
  country: string
  result: number
  percentage: number
  data: string | null

  participants?: number
  isSeparatorForAbove?: boolean
  isSeparatorForBelow?: boolean
  isBeforeSelf?: boolean
  isSelf?: boolean
}

const fetchSize = 50
const maxFetchSize = 5000

export interface IYumLeaderboardTableProps {
  goalDto: GoalWithProgress
  countries: string[]
  activityFilter: {
    countries: string[]
    divisionIds: DivisionId[]
  }
  activityView: ActivityView
  groupView: GroupView
  groupSum: boolean
  showTabs: boolean
  userId: UserId
  divisionId: DivisionId
  country: string
  token?: string
  searchValue?: string
  setTotalCount: (count: number) => void
  setTotalTeamCount: (count: number) => void
  setTotalCountryCount: (count: number) => void
  lastSync: number
  lastBonusPointsAdded: number | null
  bonusPointsFeature?: boolean
  hideTeamName?: boolean
  hideCountry?: boolean
  isFindMeLoading?: boolean
  setIsFindMeLoading?: (isLoading: boolean) => void
  getCurrentCompanyGoal({ id, token }: { id: GoalId; token?: string }): void
  hasRoleInCompany(companyId: CompanyId, role: Role): boolean
  dailyDiary: UserDailyDiaryProgress[]
  customProgress: UserCustomDailyProgress[]
  progressResults: UserDailyProgress[]
  /** Whether to use the 2026 layout refresh styling */
  compactLayout?: boolean
  divisions?: Array<{ id: string; name: string; categoryId?: string }>
  onSelfNotFound?: (notFound: boolean) => void
  excludedTeamInfo?: { teamId: DivisionId; teamName: string } | null
}

const YumLeaderboardTable = (props: IYumLeaderboardTableProps) => {
  const {
    goalDto,
    activityFilter,
    activityView,
    groupSum,
    groupView,
    userId,
    divisionId,
    country,
    showTabs,
    token,
    searchValue = '',
    setTotalCount,
    setTotalTeamCount,
    setTotalCountryCount,
    isFindMeLoading,
    setIsFindMeLoading,
    getCurrentCompanyGoal,
    lastSync,
    lastBonusPointsAdded,
    bonusPointsFeature,
    hideTeamName,
    hideCountry,
    hasRoleInCompany,
    dailyDiary,
    customProgress,
    progressResults,
    compactLayout,
    divisions,
    onSelfNotFound,
    excludedTeamInfo,
  } = props
  const {
    id: goalId,
    progressType,
    customType,
    customUnit,
    participating,
    participantsCount,
    teamRankFrom,
    countryRankFrom,
    divisionCategoryRanks,
  } = goalDto

  // Get queryClient from context
  const queryClient = useQueryClient()

  //const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([])
  const [globalFilter, setGlobalFilter] = useState<string>('')
  //const [sorting, setSorting] = useState<MRT_SortingState>([])

  const [keyboardHeight, setKeyboardHeight] = useState(0)
  const [availableHeight, setAvailableHeight] = useState(0)
  const [isMobile, setIsMobile] = useState(true)
  const tableInstanceRef = useRef(null)

  // Read leaderboard settings directly from goal
  const showGroupMembers = (goalDto as any).showGroupMembers === true
  const showGroupMembersContributions = (goalDto as any).showGroupMembersContributions === true

  // Check if the current view supports group row expansion
  const isExpandableView = useMemo(() => {
    if (!showGroupMembers) return false
    return (
      activityView === ActivityView.GROUP ||
      activityView === ActivityView.COUNTRY ||
      (typeof activityView === 'string' && activityView.startsWith('DIVISION_CATEGORY_'))
    )
  }, [showGroupMembers, activityView])

  // Modal state for viewing group members
  const [membersModal, setMembersModal] = useState<{ teamId: DivisionId; teamName: string; country?: string } | null>(
    null,
  )

  // Determine goalType and groupCategoryId based on activityView
  // Uses the shared helper function from challenges.tsx
  // NOTE: Must be defined before columns useMemo since columns depends on hasGoalForCurrentTab
  const { goalType, groupCategoryId } = useMemo(() => getGoalTypeFromActivityView(activityView), [activityView])
  const isActivityIndex = isActivityIndexType(progressType)
  const isLegacyCollective = isCollectiveType(progressType)
  const isCollectiveChallenge = isCollectiveType(progressType) || goalType === 'collective'

  // Check if a goal exists and is enabled for the current leaderboard type (individual, team, country, etc.)
  // This determines whether to show progress bars in the leaderboard
  // Also resolves the activityFormulaTarget from goals config (with fallback to main goal value)
  const { hasGoalForCurrentTab, activityFormulaTarget } = useMemo(() => {
    const fallbackTarget = goalDto.activityFormulaTarget || null

    // If no goalType is specified (backward compatibility), assume goal exists
    if (!goalType) return { hasGoalForCurrentTab: true, activityFormulaTarget: fallbackTarget }

    // Parse goals configuration from JSON string
    let goalsConfig: GoalsConfig | null = null
    try {
      if (goalDto.goals) {
        goalsConfig = JSON.parse(goalDto.goals) as GoalsConfig
      }
    } catch (error) {
      console.error('Failed to parse goals config:', error)
      // If parsing fails, assume goal exists for backward compatibility
      return { hasGoalForCurrentTab: true, activityFormulaTarget: fallbackTarget }
    }

    // If no goals config exists, use main goal (backward compatibility)
    if (!goalsConfig)
      return {
        hasGoalForCurrentTab: goalType === 'individual' && !isLegacyCollective ? true : false,
        activityFormulaTarget: fallbackTarget,
      }

    // Extract goal-specific configuration based on goalType
    // Each leaderboard type (individual, team, country, group) can have its own goal config
    let goalConfig: GoalConfig | null = null
    if (goalType === 'group' && groupCategoryId) {
      goalConfig = goalsConfig.group?.[groupCategoryId] || null
    } else if (goalType === 'individual') {
      goalConfig = goalsConfig.individual || null
    } else if (goalType === 'team') {
      goalConfig = goalsConfig.team || null
    } else if (goalType === 'country') {
      goalConfig = goalsConfig.country || null
    } else if (goalType === 'collective') {
      goalConfig = goalsConfig.collective || null
    }

    // Resolve activityFormulaTarget: goalConfig.indexTarget > fallback to main goal value
    const resolvedTarget =
      goalConfig?.indexTarget != null && goalConfig.indexTarget !== '' ? goalConfig.indexTarget : fallbackTarget

    // Return true if goal config exists and is enabled, or if no goal config (backward compatibility)
    return {
      hasGoalForCurrentTab: goalConfig ? goalConfig.enabled : false,
      activityFormulaTarget: resolvedTarget as number | null,
    }
  }, [goalDto.goals, goalDto.activityFormulaTarget, goalType, groupCategoryId, isLegacyCollective])
  const showProgress = hasGoalForCurrentTab && goalType !== 'collective'

  const showBonusPointsColumn =
    bonusPointsFeature &&
    (activityView === ActivityView.INDIVIDUAL || activityView === ActivityView.COLLECTIVE) &&
    hasRoleInCompany(goalDto.companyId, Role.ADMIN)

  // Remove columnVisibility state since we'll use initial state
  const columns = useMemo<MRT_ColumnDef<LeaderboardProgress>[]>(
    () => [
      {
        accessorKey: 'rank',
        header: 'Rank',
        size: 50,
        Cell: ({ cell }) => {
          const { rank } = cell.row.original
          return <span>{rank + '.'}</span>
        },
        enableGlobalFilter: false,
        /*filterFn: (row, columnId, filterValue) => {
          const value = row.getValue(columnId)
          return value ? value.toString().toLowerCase().includes(filterValue.toLowerCase()) : false
        },*/
      },
      {
        accessorKey: 'name',
        header: 'Name',
        Cell: ({ cell }) => {
          const original = cell.row.original
          // TODO: don't show if has bonus points
          const tooltip =
            !showBonusPointsColumn && original.isSeparatorForBelow ? (
              <BlueTooltip
                title={t`Being above the line shows that you are on the track to complete the goal by the end of the period.`}
                placement="top"
                enterTouchDelay={50}
                leaveTouchDelay={3000}
                arrow
              >
                <Box
                  sx={{ position: 'absolute', zIndex: 2, bottom: '-8px', left: '50%', marginLeft: '-11px' } as object}
                >
                  {original.isSeparatorForBelow ? <IconGoalLine /> : <IconGoalLineInverse />}
                </Box>
              </BlueTooltip>
            ) : null

          if (activityView === ActivityView.INDIVIDUAL || activityView === ActivityView.COLLECTIVE) {
            return (
              <>
                {tooltip}
                <span>
                  <span
                    id={goalId + original.userId}
                    style={{
                      display: 'inline-block',
                      verticalAlign: 'middle',
                      overflow: 'hidden',
                    }}
                  >
                    {original.name}
                  </span>
                  {(!hideCountry && original.country) || (!hideTeamName && original.teamName) ? (
                    <>
                      <br />
                      <small style={{ display: 'block', marginTop: '2px' }}>
                        {!hideCountry && original.country && props.countries && props.countries.length ? (
                          <span style={{ marginRight: 2, lineHeight: 1 }}>{countryToFlag(original.country)}</span>
                        ) : (
                          ''
                        )}
                        {!hideTeamName && original.teamName}
                      </small>
                    </>
                  ) : (
                    ''
                  )}
                </span>
              </>
            )
          } else if (activityView === ActivityView.GROUP) {
            return (
              <>
                {tooltip}
                <span>
                  <span id={goalId + original.teamId}>
                    {original.country && props.countries && props.countries.length ? (
                      <span style={{ marginRight: 2, lineHeight: 1 }}>{countryToFlag(original.country)}</span>
                    ) : (
                      ''
                    )}
                    {original.teamName}
                  </span>
                  <br />
                  {isExpandableView ? (
                    <small
                      style={{ whiteSpace: 'nowrap', cursor: 'pointer', textDecoration: 'underline' }}
                      onClick={(e) => {
                        e.stopPropagation()
                        setMembersModal({ teamId: original.teamId, teamName: original.teamName })
                      }}
                    >
                      {`${t`Participants`}: ${original.participants ? formatThousands(original.participants) : 0}`}
                    </small>
                  ) : (
                    <small style={{ whiteSpace: 'nowrap' }}>
                      {t`Participants`}: {original.participants && formatThousands(original.participants)}
                    </small>
                  )}
                </span>
              </>
            )
          } else if (activityView === ActivityView.COUNTRY) {
            return (
              <>
                {tooltip}
                <span>
                  <span id={goalId + original.country}>{countryWithFlag(original.country)}</span>
                  <br />
                  {isExpandableView ? (
                    <small
                      style={{ whiteSpace: 'nowrap', cursor: 'pointer', textDecoration: 'underline' }}
                      onClick={(e) => {
                        e.stopPropagation()
                        setMembersModal({
                          teamId: original.country as unknown as DivisionId,
                          teamName: getCountryName(original.country),
                          country: original.country,
                        })
                      }}
                    >
                      {`${t`Participants`}: ${original.participants ? formatThousands(original.participants) : 0}`}
                    </small>
                  ) : (
                    <small style={{ whiteSpace: 'nowrap' }}>
                      {t`Participants`}: {original.participants && formatThousands(original.participants)}
                    </small>
                  )}
                </span>
              </>
            )
          } else {
            return (
              <>
                {tooltip}
                <span>
                  <span id={goalId + original.teamId}>
                    {original.country && props.countries && props.countries.length ? (
                      <span style={{ marginRight: 2, lineHeight: 1 }}>{countryToFlag(original.country)}</span>
                    ) : (
                      ''
                    )}
                    {original.teamName}
                  </span>
                  <br />
                  {isExpandableView ? (
                    <small
                      style={{ whiteSpace: 'nowrap', cursor: 'pointer', textDecoration: 'underline' }}
                      onClick={(e) => {
                        e.stopPropagation()
                        setMembersModal({ teamId: original.teamId, teamName: original.teamName })
                      }}
                    >
                      {`${t`Participants`}: ${original.participants ? formatThousands(original.participants) : 0}`}
                    </small>
                  ) : (
                    <small style={{ whiteSpace: 'nowrap' }}>
                      {t`Participants`}: {original.participants && formatThousands(original.participants)}
                    </small>
                  )}
                </span>
              </>
            )
          }
        },
      },
      {
        accessorKey: 'percentage',
        header: 'Result',
        Cell: ({ cell }) => {
          const { result, percentage = 0, data, isSeparatorForBelow } = cell.row.original
          let caloriesTotal = 0
          let intensiveMinutesTotal = 0
          let stepsTotal = 0
          if (isActivityIndex && data) {
            const jsonData = JSON.parse(data)
            caloriesTotal = jsonData.caloriesTotal
            intensiveMinutesTotal = jsonData.intensiveMinutesTotal
            stepsTotal = jsonData.stepsTotal
          }
          const cellActivityFormulaTarget = isActivityIndex ? activityFormulaTarget || 0 : 0
          // If other tabs, it should be 100%
          /*if (activityView !== ActivityView.INDIVIDUAL && activityView !== ActivityView.COLLECTIVE) {
            cellActivityFormulaTarget = 100
          }*/
          // Only show progress bar if goal exists for the current tab
          // const shouldShowProgress =
          // hasGoalForCurrentTab && !isCollectiveChallenge && activityView !== ActivityView.COLLECTIVE

          // ONLY show if has bonus points
          const tooltip =
            showBonusPointsColumn && isSeparatorForBelow ? (
              <BlueTooltip
                title={t`Being above the line shows that you are on the track to complete the goal by the end of the period.`}
                placement="top"
                enterTouchDelay={50}
                leaveTouchDelay={3000}
                arrow
              >
                <Box
                  sx={{ position: 'absolute', zIndex: 2, bottom: '-8px', left: '50%', marginLeft: '-11px' } as object}
                >
                  {isSeparatorForBelow ? <IconGoalLine /> : <IconGoalLineInverse />}
                </Box>
              </BlueTooltip>
            ) : null

          return (
            <>
              {tooltip}
              <ProgressCell
                caloriesTotal={caloriesTotal}
                intensiveMinutesTotal={intensiveMinutesTotal}
                stepsTotal={stepsTotal}
                progress={showProgress ? Math.round(percentage) : undefined}
                percentage={isActivityIndex ? Math.round(percentage) : undefined}
                progressType={progressType}
                activityFormulaTarget={cellActivityFormulaTarget}
                persistenceTotal={result}
                customType={customType}
                customUnit={customUnit}
                activityView={activityView}
              />
            </>
          )
        },
        enableGlobalFilter: false,
      },
      {
        accessorKey: 'country',
        header: 'Country',
        enableHiding: true,
        Cell: ({ cell }) => {
          const { country } = cell.row.original
          return getCountryName(country)
        },
      },
      // Only include bonus points column when activityView is INDIVIDUAL and user is company admin
      ...(showBonusPointsColumn
        ? [
            {
              enableHiding: true,
              accessorKey: 'bonus',
              header: 'Bonus points',
              size: isMobile ? 50 : 70,
              Cell: ({ cell }: any) => {
                const { userId, name } = cell.row.original
                return (
                  <YumLogActivity
                    goal={goalDto}
                    autoClose={false}
                    isMeView={false}
                    isInProgress={false}
                    isBonus={true}
                    userId={userId}
                    disabled={false}
                    name={name}
                    showManualEntryButton={true}
                  />
                )
              },
              enableGlobalFilter: false,
            },
          ]
        : []),
    ],
    [
      activityView,
      isCollectiveChallenge,
      progressType,
      customType,
      customUnit,
      isMobile,
      hasGoalForCurrentTab,
      activityFormulaTarget,
    ],
  )

  const calculateHeight = () => {
    const isMobile = window.innerWidth < 992
    setIsMobile(isMobile)
    //const hasBottomMenu = isMobile
    const viewportHeight = window.innerHeight

    // Get the leaderboard element and its top position relative to the viewport
    /*const leaderboardElement = document.getElementById('leaderboard')
    const leaderboardTop = leaderboardElement ? leaderboardElement.getBoundingClientRect().top : 0
    console.log('leaderboardTop', leaderboardTop)*/
    //const leaderboardTop = hasBottomMenu ? 240 : 291

    const searchBar = 0 // isSearchVisible ? (showTabs ? 70 : 56) : 0

    const paddingTop = 16
    const header = isLeaderboard ? 0 : 56
    const tabsPaddingTop = isLeaderboard ? 0 : 16
    const tabs = isLeaderboard ? 0 : 40
    const tabsPaddingBottom = 16
    const buttonsRow = 32
    const buttonsRowBottomPadding = 16
    const bottomMenu = 59
    const bottomMenuPaddingTop = 16
    const tableTopDivider = compactLayout ? 17 : 0
    const tablePaddingBottom = 10
    const viewTabs = showTabs ? (compactLayout ? 33 : 41) : 10

    const showCollectiveNoGoalCard = compactLayout && isCollectiveChallenge && !hasGoalForCurrentTab
    const goalCard =
      compactLayout && hasGoalForCurrentTab
        ? isCollectiveChallenge || !isLeaderboard
          ? 96
          : 49
        : showCollectiveNoGoalCard
          ? 49
          : 0
    const goalCardBottomMargin = (compactLayout && hasGoalForCurrentTab) || showCollectiveNoGoalCard ? 8 : 0
    // Force-collective challenges render an extra single-row "Collective total" card
    // (52px content + 16px bottom margin) above the leaderboard table.
    const forceCollectiveTotalCard = isForceCollectiveTotal(goalDto.id) ? 68 : 0

    const safeAreaInsetTop = parseInt(
      getComputedStyle(document.documentElement).getPropertyValue('--safe-area-inset-top'),
    )
    let safeAreaInsetBottom = parseInt(
      getComputedStyle(document.documentElement).getPropertyValue('--safe-area-inset-bottom'),
    )
    safeAreaInsetBottom = safeAreaInsetBottom > 0 ? safeAreaInsetBottom - 12 : 0 //47px

    // Calculate available height
    /*const searchBar = 72
    const offset = (hasBottomMenu ? 85 : 50) + safeAreaInsetBottom
    const calculated = viewportHeight - leaderboardTop - offset - searchBar*/

    const offset =
      paddingTop +
      safeAreaInsetTop +
      header +
      tabsPaddingTop +
      tabs +
      searchBar +
      tabsPaddingBottom +
      buttonsRow +
      buttonsRowBottomPadding +
      viewTabs +
      goalCard +
      goalCardBottomMargin +
      forceCollectiveTotalCard +
      tableTopDivider +
      tablePaddingBottom +
      bottomMenuPaddingTop +
      bottomMenu +
      safeAreaInsetBottom
    let calculated = viewportHeight - offset

    if (keyboardHeight > 0) {
      calculated = calculated - keyboardHeight + bottomMenu + safeAreaInsetBottom
    }

    if (isFetchingNextPage) {
      calculated = calculated - 39
    }

    if (isMobile) {
      calculated = calculated + 10
    }

    if (token && !compactLayout) {
      if (isCollectiveChallenge) {
        calculated = calculated - 100
      } else {
        calculated = calculated + 50
      }
    }

    // if (goalDto.goals) {
    //   // Parse goals configuration from JSON string
    //   let goalsConfig: GoalsConfig | null = null
    //   try {
    //     if (goalDto.goals) {
    //       goalsConfig = JSON.parse(goalDto.goals) as GoalsConfig
    //     }
    //   } catch (error) {
    //     console.error('Failed to parse goals config:', error)
    //   }

    //   // If challenge has collective goal
    //   if (goalsConfig?.collective?.enabled) {
    //     calculated = calculated - 129
    //   }

    //   // Deduct this height if challenge has dedicated goal for this tab
    //   const goalHeaderHeight = 129
    //   if (activityView === ActivityView.INDIVIDUAL || activityView === ActivityView.COLLECTIVE) {
    //     if (goalsConfig?.individual?.enabled) {
    //       calculated = calculated - goalHeaderHeight
    //     }
    //   } else if (activityView === ActivityView.GROUP) {
    //     if (goalsConfig?.team?.enabled) {
    //       calculated = calculated - goalHeaderHeight
    //     }
    //   } else if (activityView === ActivityView.COUNTRY) {
    //     if (goalsConfig?.country?.enabled) {
    //       calculated = calculated - goalHeaderHeight
    //     }
    //   } else {
    //     if (groupCategoryId && goalsConfig?.group[groupCategoryId]?.enabled) {
    //       calculated = calculated - goalHeaderHeight
    //     }
    //   }
    // }

    // Subtract excluded team banner height if visible
    const excludedBanner = document.getElementById('excluded-team-banner')
    if (excludedBanner) {
      calculated = calculated - excludedBanner.offsetHeight
    }

    const minHeight = 100
    setAvailableHeight(calculated < minHeight ? minHeight : calculated)
  }

  useEffect(() => {
    // Calculate on mount and on window resize
    calculateHeight()
    window.addEventListener('resize', calculateHeight)
    return () => window.removeEventListener('resize', calculateHeight)
  }, [])

  const tableContainerRef = useRef<HTMLDivElement>(null) //we can get access to the underlying TableContainer element and react to its scroll events
  const rowVirtualizerInstanceRef = useRef<MRT_Virtualizer<HTMLDivElement, HTMLTableRowElement>>(null) //we can get access to the underlying Virtualizer instance and call its scrollToIndex method

  const debouncedSearch = useCallback(
    (value: string) => {
      // Only update if the value actually changed
      if (value !== globalFilter) {
        setGlobalFilter(value)
        // Reset table position when search changes
        try {
          rowVirtualizerInstanceRef.current?.scrollToIndex?.(0)
        } catch (error) {
          //
        }
      }
    },
    [globalFilter],
  )

  // Handle status tap event to scroll to top
  useEffect(() => {
    const handleStatusTap = () => {
      //console.log('YumLeaderboardTable::handleStatusTap')
      try {
        rowVirtualizerInstanceRef.current?.scrollToIndex?.(0)
      } catch (error) {
        // Silently handle any errors
      }
    }

    window.addEventListener('statusTap', handleStatusTap)

    // Cleanup function to remove event listener
    return () => {
      window.removeEventListener('statusTap', handleStatusTap)
    }
  }, []) // Empty dependency array since we don't need to recreate the listener

  const [isScrollEnabled, setIsScrollEnabled] = useState(true)

  useEffect(() => {
    const timer = setTimeout(() => {
      // Only update search if scrolling is enabled (not during find me)
      if (isScrollEnabled) {
        debouncedSearch(searchValue)
      }
    }, 400)

    return () => clearTimeout(timer)
  }, [searchValue, debouncedSearch, isScrollEnabled])

  const [ofPeriodPassedPercentage, setOfPeriodPassedPercentage] = useState(0)

  // Helper function to clear cache and keep only first page
  const clearCacheAndKeepFirstPage = useCallback(
    async (
      prevGlobalFilter?: string,
      prevActivityFilter?: { countries: string[]; divisionIds: DivisionId[] },
      prevActivityView?: ActivityView,
      prevGroupSum?: boolean,
      prevGroupView?: GroupView,
    ) => {
      //console.log('clearCacheAndKeepFirstPage')

      // Scroll to top first
      try {
        rowVirtualizerInstanceRef.current?.scrollToIndex?.(0)
      } catch (error) {
        // Silently handle any errors
      }

      // Use previous values if provided, otherwise use current values
      const queryKey = [
        'table-data',
        prevGlobalFilter ?? globalFilter,
        prevActivityFilter ?? activityFilter,
        prevActivityView ?? activityView,
        prevGroupSum ?? groupSum,
        prevGroupView ?? groupView,
      ]
      try {
        // Check if there are any active queries before canceling
        //const activeQueries = queryClient.getQueryCache().findAll(queryKey)

        // Only cancel if there are active queries
        //if (activeQueries.length > 0) {
        // Use a more gentle approach to cancel queries
        await queryClient.cancelQueries({
          queryKey,
          exact: true,
        })
        //}
      } catch (error) {
        // Silently handle cancellation errors
        console.log('Query cancellation error (non-critical):', error)
      }

      // Reset to first page by setting the data to only the first page
      queryClient.setQueryData(queryKey, (oldData: InfiniteData<GetPeopleLeaderboardProgress$Response> | undefined) => {
        if (!oldData?.pages?.[0]) return undefined
        return {
          pages: [oldData.pages[0]],
          pageParams: [0],
        }
      })
    },
    [queryClient, globalFilter, activityFilter, activityView, groupSum, groupView, rowVirtualizerInstanceRef],
  )

  // Use a ref to track if this is the first mount
  const isFirstMount = useRef(true)

  // Store previous values in refs
  const prevActivityViewRef = useRef<ActivityView>(activityView)
  const prevGlobalFilterRef = useRef<string>(globalFilter)
  const prevActivityFilterRef = useRef<{ countries: string[]; divisionIds: DivisionId[] }>(activityFilter)
  const prevGroupSumRef = useRef<boolean>(groupSum)
  const prevGroupViewRef = useRef<GroupView>(groupView)

  // Clear query cache and keep only first page when activityView changes
  useEffect(() => {
    // Skip execution on first mount
    if (isFirstMount.current) {
      isFirstMount.current = false
      return
    }

    // Handle the async function with previous values
    clearCacheAndKeepFirstPage(
      prevGlobalFilterRef.current,
      prevActivityFilterRef.current,
      prevActivityViewRef.current,
      prevGroupSumRef.current,
      prevGroupViewRef.current,
    )
    //refetch()

    // Update previous values after the effect runs
    prevActivityViewRef.current = activityView
    prevGlobalFilterRef.current = globalFilter
    prevActivityFilterRef.current = activityFilter
    prevGroupSumRef.current = groupSum
    prevGroupViewRef.current = groupView
  }, [activityView, globalFilter, activityFilter, groupSum, groupView])

  // Cancel any pending queries when component unmounts or when filters change
  /*useEffect(() => {
    return () => {
      // Cancel any pending queries for this specific query key
      console.log('hook to cancel/abort')
      queryClient.cancelQueries({
        queryKey: ['table-data', globalFilter, activityFilter, activityView, groupSum, groupView],
      })
      // Also abort any ongoing goToPage operation
      if (abortControllerRef.current) {
        console.log('aborting current request')
        abortControllerRef.current.abort()
      }
    }
  }, [queryClient, globalFilter, activityFilter, activityView, groupSum, groupView])*/

  const { data, fetchNextPage, isError, isFetching, isLoading, refetch, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery<GetPeopleLeaderboardProgress$Response>({
      queryKey: ['table-data', globalFilter, activityFilter, activityView, groupSum, groupView],
      queryFn: async ({ pageParam = 0, signal }) => {
        // Skip this request if we're in the middle of a Find Me operation
        if (findMeInProgress.current) {
          console.log('Skipping normal query - Find Me in progress')
          // Return an empty result that matches the expected shape but preserve the real totalRowCount
          return {
            data: [],
            totalRowCount: realTotalRowCount.current,
            ofPeriodPassedPercentage: 0,
          }
        }

        /*console.log('Query triggered with params:', {
          pageParam,
          globalFilter,
          activityFilter,
          activityView,
          groupSum,
          groupView,
          goalDtoParticipantsCount: goalDto.participantsCount,
        })*/
        // Check if this is a DivisionCategory view
        const isDivisionCategoryView = typeof activityView === 'string' && activityView.startsWith('DIVISION_CATEGORY_')
        const categoryId = isDivisionCategoryView ? activityView.replace('DIVISION_CATEGORY_', '') : null

        const endpoint = isDivisionCategoryView
          ? 'getGroupLeaderboardProgress'
          : activityView === ActivityView.GROUP
            ? 'getTeamLeaderboardProgress'
            : activityView === ActivityView.COUNTRY
              ? 'getCountryLeaderboardProgress'
              : 'getPeopleLeaderboardProgress'

        const start = pageParam * fetchSize
        const size = fetchSize
        const search = globalFilter ?? ''

        const params = isDivisionCategoryView
          ? { goalId, categoryId, groupSum, groupView, start, size, search, token }
          : activityView === ActivityView.GROUP
            ? { goalId, groupSum, groupView, start, size, search, token }
            : activityView === ActivityView.COUNTRY
              ? { goalId, groupSum, token }
              : {
                  goalId,
                  ...activityFilter,
                  token,
                  start,
                  size,
                  search,
                }

        try {
          const response = (await ApiClient.post(
            apiUrl(`/rpc/${endpoint}`),
            params,
            { signal }, // Add signal to the request
          )) as GetPeopleLeaderboardProgress$Response

          // Validate response structure and sanitize data
          if (!response) {
            console.error('Empty response from API')
            return { data: [], totalRowCount: 0, ofPeriodPassedPercentage: 0 }
          }

          // Ensure response.data is an array, even if API returns something else
          if (!Array.isArray(response.data)) {
            console.error('API returned non-array data:', response.data)
            response.data = []
          }

          // Filter out null/undefined entries from the data array
          response.data = response.data.filter(Boolean)

          if (hasGoalForCurrentTab) {
            setOfPeriodPassedPercentage(response.ofPeriodPassedPercentage)
          }
          if (!globalFilter) {
            if (isDivisionCategoryView || activityView === ActivityView.GROUP) {
              setTotalTeamCount(response.totalRowCount)
            } else if (activityView === ActivityView.COUNTRY) {
              setTotalCountryCount(response.totalRowCount)
            } else {
              setTotalCount(response.totalRowCount)
            }
            // Store the real total row count for use during Find Me
            realTotalRowCount.current = response.totalRowCount
          }
          return response
        } catch (error) {
          console.error('Error fetching leaderboard data:', error)
          // Return a valid empty response on error
          return { data: [], totalRowCount: 0, ofPeriodPassedPercentage: 0 }
        }
      },
      getNextPageParam: (lastGroup, groups) => {
        // If we have no data or the last group has no data, don't fetch more
        if (!lastGroup || !lastGroup.data || lastGroup.data.length === 0) {
          return undefined
        }

        // Calculate total fetched rows by summing up all data arrays
        const totalFetchedRows = groups.reduce((acc, group) => acc + (group.data?.length || 0), 0)

        // If we've fetched all data (based on totalRowCount), don't fetch more
        if (totalFetchedRows >= lastGroup.totalRowCount) {
          return undefined
        }

        // Calculate the next page index based on total fetched rows
        return Math.floor(totalFetchedRows / fetchSize)
      },
      keepPreviousData: false,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    })

  // Scroll to top when search results update
  useEffect(() => {
    // Only trigger on search-related data changes, not on pagination
    if (isFetching && globalFilter && !isFetchingNextPage) {
      try {
        rowVirtualizerInstanceRef.current?.scrollToIndex?.(0)
      } catch (error) {
        // Silently ignore scroll errors
      }
    }
  }, [isFetching, globalFilter, isFetchingNextPage])

  // Create an AbortController for the goToPage function
  const abortControllerRef = useRef<AbortController | null>(null)

  // Track if a find me operation is in progress
  const findMeInProgress = useRef(false)

  // Store the real total row count to use during Find Me
  const realTotalRowCount = useRef(0)

  // Cleanup function to abort any ongoing requests
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  // Add check for whether "Find me" should be shown
  const shouldShowFindMe = useMemo(() => {
    // Don't show if we're not participating
    if (!participating) return false

    // If we're not in individual view, don't show "Find me"
    if (activityView !== ActivityView.INDIVIDUAL) return false

    // Check division filter
    if (activityFilter?.divisionIds.length > 0 && !activityFilter?.divisionIds.includes(divisionId)) {
      return false
    }

    // Check country filter
    if (activityFilter?.countries.length > 0 && !activityFilter?.countries.includes(country)) {
      return false
    }

    return true
  }, [activityView, activityFilter?.divisionIds, activityFilter?.countries, divisionId, country, participating])

  async function goToPage(userRank: number, optimalFetchSize: number) {
    console.log('goToPage', { userRank, optimalFetchSize })

    // Abort any current requests
    if (abortControllerRef.current) {
      console.log('Aborting current request')
      abortControllerRef.current.abort()
    }

    // Create new AbortController for this goToPage call
    abortControllerRef.current = new AbortController()

    try {
      // For Find Me, we always want to use the unfiltered data regardless of current search
      const findMeQueryKey = [
        'table-data',
        '', // Empty search string for Find Me
        activityFilter,
        activityView,
        groupSum,
        groupView,
      ]

      // Get current data with empty search for Find Me
      let currentData = queryClient.getQueryData<InfiniteData<GetPeopleLeaderboardProgress$Response>>(findMeQueryKey)

      // If no data exists with empty search, initialize with empty pages
      if (!currentData) {
        currentData = {
          pages: [],
          pageParams: [],
        }
      }

      // Calculate total rows we already have
      let currentTotalRows = currentData?.pages?.reduce((acc, page) => acc + (page.data?.length || 0), 0) || 0
      console.log('Start total rows:', currentTotalRows)

      // Add a safety counter to prevent infinite loops
      let loopCounter = 0
      const maxLoops = 10 // Maximum number of fetch attempts

      // Keep fetching until we reach the user's rank or run out of data
      while (
        currentData?.pages &&
        currentTotalRows < userRank &&
        !abortControllerRef.current.signal.aborted &&
        loopCounter < maxLoops
      ) {
        loopCounter++

        // If we've made multiple requests and rows aren't increasing, break to prevent infinite loop
        if (loopCounter > 3 && currentTotalRows === 0) {
          console.log('Breaking goToPage loop - no data after multiple attempts')
          break
        }

        // Calculate start position based on the current data
        let start: number
        if (currentData.pages.length === 0) {
          // First page
          start = 0
        } else {
          // Subsequent pages start after all current rows
          start = currentTotalRows
        }

        // Calculate the size for this request
        let size: number
        if (currentData.pages.length === 0) {
          // First page uses normal fetch size
          size = fetchSize
        } else {
          // For subsequent pages, calculate remaining rows needed
          const remainingRows = userRank - currentTotalRows
          if (remainingRows <= optimalFetchSize) {
            // For the last request, round up to nearest fetchSize
            const roundedSize = Math.ceil(remainingRows / fetchSize) * fetchSize
            // If we're less than half a fetchSize away from the rounded size, just add one more fetchSize
            /*console.log(
              'Remaining Rows',
              remainingRows,
              'optimalFetchSize',
              optimalFetchSize,
              'roundedSize',
              roundedSize,
              'roundedSize - remainingRows',
              roundedSize - remainingRows,
              'fetchSize',
              fetchSize,
            )*/
            if (roundedSize - remainingRows < fetchSize / 2) {
              size = roundedSize + fetchSize
            } else {
              size = roundedSize
            }
            // console.log('size', size)
          } else {
            // For intermediate requests, use optimal fetch size
            size = optimalFetchSize
          }
        }

        // Must use empty string for search to find the actual rank without filtering
        const search = ''

        console.log('Find Me request:', { start, size, loop: loopCounter })

        // Check if this is a DivisionCategory view
        const isDivisionCategoryView = typeof activityView === 'string' && activityView.startsWith('DIVISION_CATEGORY_')
        const categoryId = isDivisionCategoryView ? activityView.replace('DIVISION_CATEGORY_', '') : null

        // Determine the endpoint based on activity view
        const endpoint = isDivisionCategoryView
          ? 'getGroupLeaderboardProgress'
          : activityView === ActivityView.GROUP
            ? 'getTeamLeaderboardProgress'
            : activityView === ActivityView.COUNTRY
              ? 'getCountryLeaderboardProgress'
              : 'getPeopleLeaderboardProgress'

        const params = isDivisionCategoryView
          ? { goalId, categoryId, groupSum, groupView, start, size, search, token }
          : activityView === ActivityView.GROUP
            ? { goalId, groupSum, groupView, start, size, search, token }
            : activityView === ActivityView.COUNTRY
              ? { goalId, groupSum, token }
              : {
                  goalId,
                  ...activityFilter,
                  token,
                  start,
                  size,
                  search,
                }

        try {
          const response = (await ApiClient.post(apiUrl(`/rpc/${endpoint}`), params, {
            signal: abortControllerRef.current.signal,
          })) as GetPeopleLeaderboardProgress$Response

          // Update the query data with the new response (use the findMeQueryKey)
          queryClient.setQueryData(
            findMeQueryKey,
            (oldData: InfiniteData<GetPeopleLeaderboardProgress$Response> | undefined) => {
              if (!oldData) {
                return {
                  pages: [response],
                  pageParams: [0],
                }
              }
              return {
                ...oldData,
                pages: [...oldData.pages, response],
                pageParams: [...oldData.pageParams, oldData.pageParams.length],
              }
            },
          )

          // Check if aborted before continuing
          if (abortControllerRef.current.signal.aborted) {
            break
          }

          // Wait a tick so that React Query's store updates
          await new Promise((r) => setTimeout(r, 0))

          // Re-read data from the store and recalculate total rows
          currentData = queryClient.getQueryData(findMeQueryKey)
          currentTotalRows = currentData?.pages?.reduce((acc, page) => acc + (page.data?.length || 0), 0) || 0
          console.log('new currentTotalRows', currentTotalRows)
        } catch (error) {
          if (error instanceof Error && error.name === 'AbortError') {
            console.log('Request aborted')
            break
          }
          console.error('Error fetching data:', error)
          break
        }
      }

      // After Find Me completes, refresh the main query data if globalFilter is empty
      if (globalFilter === '') {
        // Get the data we just fetched
        const findMeData = queryClient.getQueryData<InfiniteData<GetPeopleLeaderboardProgress$Response>>(findMeQueryKey)

        // If we have Find Me data, use it for the main query
        if (findMeData) {
          // Ensure the totalRowCount is preserved in the data
          const modifiedData: InfiniteData<GetPeopleLeaderboardProgress$Response> = {
            ...findMeData,
            pages: findMeData.pages.map((page, index: number) => {
              if (index === 0) {
                return {
                  ...page,
                  totalRowCount: realTotalRowCount.current, // Use the real total row count
                }
              }
              return page
            }),
          }

          // Update the main query data
          queryClient.setQueryData(
            ['table-data', globalFilter, activityFilter, activityView, groupSum, groupView],
            modifiedData,
          )
        }
      }
    } catch (error) {
      // Only log error if it's not an abort error
      if (error instanceof Error && error.name !== 'AbortError') {
        console.error('Error in goToPage:', error)
      }
    } finally {
      // Don't re-enable scrolling here - that should happen only in the onFindMe function
      console.log('goToPage complete')
    }
  }

  useEffect(() => {
    // When tab changes, clear and the close search
    setGlobalFilter('')
  }, [activityView])

  const isLeaderboard = !!token

  // Parse divisionCategoryRanks and get currentDivisionId for DivisionCategory views
  const currentDivisionId = useMemo(() => {
    // Check if activityView is a string before calling string methods
    // This matches the pattern used elsewhere in this file (lines 624 and 883)
    if (typeof activityView !== 'string' || !activityView.startsWith('DIVISION_CATEGORY_')) {
      return divisionId
    }

    const categoryId = activityView.replace('DIVISION_CATEGORY_', '')
    const parsedRanks =
      typeof divisionCategoryRanks === 'string' ? JSON.parse(divisionCategoryRanks) : divisionCategoryRanks

    return parsedRanks?.[categoryId]?.id || divisionId
  }, [activityView, divisionCategoryRanks, divisionId])

  // Recalculate height when activity view or group category changes
  useEffect(() => {
    calculateHeight()
  }, [isFetchingNextPage, keyboardHeight, activityView, groupCategoryId])

  // @ts-ignore
  const flatData = useMemo(() => {
    // @ts-ignore
    const flatted = data?.pages?.flatMap((page) => {
      // Additional safeguard for null/undefined data
      if (!page || !page.data) return []

      // Ensure we're only returning valid data entries
      return Object.values(page.data).filter(Boolean)
    }) as LeaderboardProgress[]

    // Track if a separator has already been set - only one separator row allowed in total
    let separatorAlreadySet = false

    const mapped =
      flatted?.map((row: LeaderboardProgress, index: number) => {
        if (!row) {
          return null
        }

        // Add safeguards for missing properties
        const percentage = row.percentage || 0

        let name, compareId, isSelf
        if (!isLeaderboard) {
          if (activityView === ActivityView.INDIVIDUAL || activityView === ActivityView.COLLECTIVE) {
            compareId = row.userId
            isSelf = userId === compareId
          } else if (activityView === ActivityView.GROUP) {
            compareId = row.teamId
            if (props.groupView === GroupView.COUNTRIES_MERGED) {
              isSelf = divisionId === compareId
            } else {
              isSelf = divisionId === compareId && country === row.country
            }
          } else if (activityView === ActivityView.COUNTRY) {
            compareId = row.country
            isSelf = country === compareId
          } else {
            compareId = row.teamId
            if (props.groupView === GroupView.COUNTRIES_MERGED) {
              isSelf = currentDivisionId === compareId
            } else {
              isSelf = currentDivisionId === compareId && country === row.country
            }
          }
        }

        let target = 100
        let adjustedOfPeriodPassedPercentage = ofPeriodPassedPercentage

        // Custom target for activity index (ALL type) - use goals config target with fallback
        if (progressType === ProgressType.ALL && activityFormulaTarget && ofPeriodPassedPercentage !== null) {
          target = activityFormulaTarget
          adjustedOfPeriodPassedPercentage = Math.floor(ofPeriodPassedPercentage * (target / 100))
        }

        const isCompleted = percentage >= target

        let isSeparatorForAbove = false
        let isSeparatorForBelow = false

        // Only calculate separator if one hasn't been set yet
        // Skip separator for collective challenges (no individual percentage to compare)
        if (hasGoalForCurrentTab && !separatorAlreadySet && !isCollectiveChallenge) {
          const prevRow = flatted?.[index - 1] || { percentage: undefined }
          const nextRow = flatted?.[index + 1] || { percentage: undefined }

          if (adjustedOfPeriodPassedPercentage || adjustedOfPeriodPassedPercentage === 0) {
            // For first row
            isSeparatorForAbove = prevRow.percentage === undefined && percentage < adjustedOfPeriodPassedPercentage
            // For all the rows
            isSeparatorForBelow =
              percentage >= adjustedOfPeriodPassedPercentage &&
              (!nextRow.percentage || nextRow.percentage < adjustedOfPeriodPassedPercentage)
          }
        }

        if (isSeparatorForBelow && ofPeriodPassedPercentage === 0) {
          isSeparatorForBelow = false
        }

        // Mark separator as set if either flag is true (prevents multiple separators)
        if (isSeparatorForAbove || isSeparatorForBelow) {
          separatorAlreadySet = true
        }

        /*if (isSeparatorForAbove) {
          console.log('isSeparatorForAbove', index, row.name)
        }*/

        const alignRight = isCollectiveChallenge

        return {
          ...row,
          isCompleted,
          isSelf,
          isSeparatorForBelow,
          isSeparatorForAbove,
          progressType,
          target,
          alignRight,
          isBeforeSelf: false,
        }
      }) ?? []

    // We need the isBeforeSelf flag to prevent table line in the previous row.
    if (mapped.length) {
      mapped.some((row, index, array) => {
        if (row?.isSelf && index > 0) {
          const prevRow = array[index - 1]
          if (prevRow) {
            prevRow.isBeforeSelf = true
          }
          return true
        }
        return false
      })
    }

    return mapped.filter(Boolean) // Additional safeguard to remove null items
  }, [data, ofPeriodPassedPercentage, currentDivisionId])

  const count = useMemo(() => {
    switch (activityView) {
      case ActivityView.INDIVIDUAL:
        return participantsCount
      case ActivityView.GROUP:
        return teamRankFrom
      case ActivityView.COUNTRY:
        return countryRankFrom
      default:
        return 10
    }
  }, [activityView, participantsCount, teamRankFrom, countryRankFrom])

  // Calculate the total row count considering both normal data and Find Me state
  const totalDBRowCount = findMeInProgress.current ? realTotalRowCount.current : (data?.pages?.[0]?.totalRowCount ?? 0)
  const totalFetched = flatData.length

  const fetchMoreOnBottomReached = useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (containerRefElement && isScrollEnabled) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement
        const bottomThreshold = 1500
        if (
          scrollHeight - scrollTop - clientHeight < bottomThreshold &&
          !isFetching &&
          totalFetched < totalDBRowCount &&
          totalDBRowCount > 0
        ) {
          fetchNextPage()
        }
      }
    },
    [fetchNextPage, isFetching, totalFetched, totalDBRowCount, isScrollEnabled],
  )

  useEffect(() => {
    if (data?.pages && data.pages.length > 0 && isScrollEnabled) {
      fetchMoreOnBottomReached(tableContainerRef.current)
    }
  }, [fetchMoreOnBottomReached, data?.pages, isScrollEnabled])

  // Targeted background lookup that confirms whether the user's team appears in the leaderboard
  // at all. Without it, infinite-scroll pages that haven't loaded yet would be misread as exclusion.
  const isTeamView =
    activityView === ActivityView.GROUP ||
    (typeof activityView === 'string' && activityView.startsWith('DIVISION_CATEGORY_'))
  const selfFoundInLoadedData = useMemo(() => flatData.some((row: any) => row?.isSelf), [flatData])
  const { data: teamLookupData, isFetching: isTeamLookupFetching } = useQuery<GetPeopleLeaderboardProgress$Response>({
    queryKey: [
      'team-existence-check',
      goalId,
      activityView,
      groupSum,
      groupView,
      excludedTeamInfo?.teamId,
      excludedTeamInfo?.teamName,
    ],
    queryFn: async () => {
      const isDivisionCategoryView = typeof activityView === 'string' && activityView.startsWith('DIVISION_CATEGORY_')
      const categoryId = isDivisionCategoryView ? activityView.replace('DIVISION_CATEGORY_', '') : null
      const endpoint = isDivisionCategoryView ? 'getGroupLeaderboardProgress' : 'getTeamLeaderboardProgress'
      const params = isDivisionCategoryView
        ? {
            goalId,
            categoryId,
            groupSum,
            groupView,
            start: 0,
            size: 100,
            search: excludedTeamInfo?.teamName ?? '',
            token,
          }
        : {
            goalId,
            groupSum,
            groupView,
            start: 0,
            size: 100,
            search: excludedTeamInfo?.teamName ?? '',
            token,
          }
      return (await ApiClient.post(apiUrl(`/rpc/${endpoint}`), params)) as GetPeopleLeaderboardProgress$Response
    },
    enabled: !!excludedTeamInfo && isTeamView && !selfFoundInLoadedData,
    // staleTime: 10000,
  })
  const teamFoundInLookup = useMemo(() => {
    if (!teamLookupData?.data || !excludedTeamInfo) return false
    return teamLookupData.data.some((row: any) => row?.teamId === excludedTeamInfo.teamId)
  }, [teamLookupData, excludedTeamInfo])

  // Notify parent if user's team is not found anywhere in the leaderboard (excluded due to min team size)
  useEffect(() => {
    if (!onSelfNotFound || isLoading || isFetching) return
    if (!isTeamView) {
      onSelfNotFound(false)
      return
    }
    if (selfFoundInLoadedData) {
      onSelfNotFound(false)
      return
    }
    // Wait for the targeted lookup before claiming exclusion to avoid a flash while pages stream in.
    if (!excludedTeamInfo || isTeamLookupFetching || teamLookupData === undefined) {
      onSelfNotFound(false)
      return
    }
    onSelfNotFound(!teamFoundInLookup)
    // Recalculate height after banner visibility changes
    setTimeout(calculateHeight, 50)
  }, [
    isLoading,
    isFetching,
    isTeamView,
    selfFoundInLoadedData,
    excludedTeamInfo,
    isTeamLookupFetching,
    teamLookupData,
    teamFoundInLookup,
    onSelfNotFound,
  ])

  const getSearchableText = (row: LeaderboardProgress): string => {
    const parts: string[] = []
    if (row.name) parts.push(row.name)
    if (row.teamName) parts.push(row.teamName)
    if (row.country) parts.push(getCountryName(row.country))
    return parts.join(' ')
  }

  const customFilterFn: MRT_FilterFn<LeaderboardProgress> = (row, columnId, filterValue) => {
    if (!filterValue) return true
    const searchableText = getSearchableText(row.original)
    return searchableText.toLowerCase().includes(String(filterValue).toLowerCase())
  }

  useEffect(() => {
    if (isScrollEnabled) {
      fetchNextPage()
    }
  }, [fetchNextPage, isScrollEnabled])

  const onFindMe = () => {
    if (setIsFindMeLoading) {
      setIsFindMeLoading(true)
    }

    console.log('onFindMe triggered, current search:', globalFilter)

    // Mark Find Me operation as in progress to block normal queries
    findMeInProgress.current = true

    // Safety timeout to ensure loader is reset after a reasonable time
    const safetyTimeout = setTimeout(() => {
      if (setIsFindMeLoading) {
        setIsFindMeLoading(false)
      }
      // Ensure findMeInProgress is reset even if there's an error
      findMeInProgress.current = false
    }, 30000) // 30 seconds max loading time

    // Get the user's rank from goalDto
    const userRank = goalDto.position
    if (userRank) {
      console.log('User rank:', userRank)

      // Cancel any existing queries to prevent race conditions
      queryClient.cancelQueries({
        queryKey: ['table-data'],
      })

      // Temporarily disable scrolling to prevent automatic fetch triggers
      console.log('Disabling scroll triggers')
      setIsScrollEnabled(false)

      // Prepare for "Find Me" by clearing search but without triggering new requests
      const currentGlobalFilter = globalFilter
      if (currentGlobalFilter) {
        console.log('Clearing search filter:', currentGlobalFilter)
        // Internally update the state without triggering downstream effects yet
        setGlobalFilter('')
      }

      // Wait for the data to be refetched after filter reset
      const waitForData = async () => {
        try {
          // Calculate the optimal fetch size for this operation
          const optimalFetchSize = Math.min(maxFetchSize, Math.max(fetchSize, userRank))
          // console.log('optimalFetchSize', optimalFetchSize, 'userRank', userRank)

          // Calculate total fetched rows so far
          const totalFetchedRows = data?.pages?.reduce((acc, page) => acc + (page.data?.length || 0), 0) || 0
          // console.log('totalFetchedRows', totalFetchedRows)

          // If we need to fetch more data to get to the user's position
          if (totalFetchedRows < userRank) {
            // Use the improved goToPage with larger fetch size for find me operation
            await goToPage(userRank, optimalFetchSize)

            // After fetching all pages, scroll to the user's position
            try {
              rowVirtualizerInstanceRef.current?.scrollToIndex(userRank - 1, { align: 'center' })
            } catch (error) {
              //
            }
          } else {
            //console.log('just scrolling - userRank', userRank)
            // If we already have the user's page, scroll to their position
            try {
              rowVirtualizerInstanceRef.current?.scrollToIndex(userRank - 1, { align: 'center', behavior: 'smooth' })
            } catch (error) {
              //
            }
          }
        } catch (error) {
          console.error('Error in Find Me:', error)
        } finally {
          clearTimeout(safetyTimeout)
          if (setIsFindMeLoading) {
            setIsFindMeLoading(false)
          }

          // Mark Find Me operation as complete
          findMeInProgress.current = false
          setIsScrollEnabled(true)

          // Trigger a refetch to ensure we have the latest data with correct counts
          // setTimeout(() => {
          //   // Re-enable scrolling after operation completes
          //   console.log('Re-enabling scroll triggers')
          //   setIsScrollEnabled(true)

          //   // Force a refetch if needed
          //   if (flatData.length >= fetchSize) {
          //     console.log('Refreshing data after Find Me')
          //     refreshData()
          //   }
          // }, 100)
        }
      }

      waitForData()
    } else {
      clearTimeout(safetyTimeout)
      if (setIsFindMeLoading) {
        setIsFindMeLoading(false)
      }
      // Mark Find Me operation as complete if no user rank
      findMeInProgress.current = false
    }
  }
  const refreshData = async () => {
    try {
      await clearCacheAndKeepFirstPage()
      await refetch()
    } catch (error) {
      console.error('Data refresh error:', error)
    }
  }

  useEffect(() => {
    let appStateChangeListener: PluginListenerHandle | undefined
    const setListener = async () => {
      appStateChangeListener = await App.addListener('appStateChange', async ({ isActive }) => {
        focusManager.setFocused(isActive)
        if (isActive) {
          refreshData()
        }
      })
    }
    setListener()

    return () => {
      if (appStateChangeListener) {
        try {
          appStateChangeListener.remove()
        } catch (error) {
          //
        }
      }
    }
  }, [refetch, clearCacheAndKeepFirstPage])

  const isFirstChange = useRef(true)
  useEffect(() => {
    if (isFirstChange.current) {
      isFirstChange.current = false
      return
    }
    refreshData()
  }, [refetch, participating, lastSync, lastBonusPointsAdded])

  React.useEffect(() => {
    let timerId: NodeJS.Timeout | undefined
    if (token) {
      const clearAndRefetch = async () => {
        await clearCacheAndKeepFirstPage()
        getCurrentCompanyGoal({ id: goalDto.id, token })
        await refetch()
      }
      timerId = setInterval(clearAndRefetch, 300000) // 5 minutes
      // For testing
      //timerId = setInterval(clearAndRefetch, 5000) // 5 seconds
    }
    return () => {
      if (timerId) {
        clearInterval(timerId)
      }
    }
  }, [token, refetch])

  // Reset the table when filters change
  useEffect(() => {
    try {
      rowVirtualizerInstanceRef.current?.scrollToIndex?.(0)
    } catch (error) {
      //
    }
  }, [globalFilter, activityFilter, activityView, groupSum, groupView])

  // Monitor isFindMeLoading from parent - if it changes to true, trigger onFindMe
  const prevFindMeLoadingRef = useRef(false)
  useEffect(() => {
    //console.log('isFindMeLoading', isFindMeLoading)
    // Check if isFindMeLoading changed from false to true
    if (isFindMeLoading && !prevFindMeLoadingRef.current) {
      //console.log('Find Me triggered from parent')
      // Use setTimeout to ensure this happens after the state update
      setTimeout(() => {
        onFindMe()
      }, 0)
    }
    prevFindMeLoadingRef.current = !!isFindMeLoading
  }, [isFindMeLoading])

  // const header = compactLayout ? (
  //   <div style={{ width: '100%', height: '1px', position: 'relative', backgroundColor: '#e3e3e8', margin: '8px 0' }} />
  // ) : // <YumChallengeGoal
  // //   goalType={goalType}
  // //   goal={goalDto}
  // //   dailyDiary={dailyDiary}
  // //   customProgress={customProgress}
  // //   progressResults={progressResults}
  // //   showDetails={false}
  // //   //toggleShowDetails={toggleShowDetails}
  // //   toggleShowDetails={() => {}}
  // //   isMeView={false}
  // //   isLeaderboard={!!token}
  // //   lightGrayBackground={true}
  // //   groupCategoryId={groupCategoryId}
  // // />
  // null

  const allUsersBelowLine = flatData[0]?.isSeparatorForAbove

  return (
    <div id="leaderboard" style={{ position: 'relative' }}>
      {allUsersBelowLine && (
        <BlueTooltip
          title={t`Being above the line shows that you are on the track to complete the goal by the end of the period.`}
          placement="top"
          enterTouchDelay={50}
          leaveTouchDelay={3000}
          arrow
          PopperProps={
            {
              //container: document.getElementById('content'),
              //disablePortal: true,
            }
          }
        >
          <Box
            sx={
              {
                position: 'absolute',
                zIndex: 2,
                left: 0,
                right: 0,
                borderTop: `2px solid ${darkGreen}`,
              } as object
            }
          >
            <Box
              sx={
                {
                  position: 'absolute',
                  zIndex: 2,
                  top: '-13px',
                  left: '50%',
                  marginLeft: '-11px',
                  '@media (max-width: 767px)': {
                    top: '-11px',
                  },
                } as object
              }
            >
              <IconGoalLine />
              {/* {original.isSeparatorForBelow ? <IconGoalLine /> : <IconGoalLineInverse />} */}
            </Box>
          </Box>
        </BlueTooltip>
      )}
      <MaterialReactTable<LeaderboardProgress>
        tableInstanceRef={tableInstanceRef}
        columns={columns}
        data={flatData.filter(Boolean) as LeaderboardProgress[]}
        enablePagination={false}
        enableTopToolbar={true}
        enableBottomToolbar={true}
        // Render custom top toolbar with a div
        renderTopToolbar={({ table }) => null}
        enableRowNumbers={false}
        enableFilterMatchHighlighting={false}
        enableRowVirtualization
        manualFiltering={activityView != ActivityView.COUNTRY}
        manualSorting={true}
        enableColumnFilters={false}
        enableGlobalFilter={true}
        /*enableHiding={true}*/
        localization={{
          noRecordsToDisplay: t`No matches found.`,
          noResultsFound: t`No matches found.`,
        }}
        renderEmptyRowsFallback={({ table }) => {
          // Don't show empty state message while loading
          if (isFetching || isLoading) return null
          return <div style={{ padding: '1rem', textAlign: 'center', color: '#24284e80' }}>{t`No matches found.`}</div>
        }}
        muiTableContainerProps={{
          ref: tableContainerRef,
          sx: {
            maxHeight: `${availableHeight}px`,
            position: 'relative', // Add this to position the loading indicator
            paddingBottom: '10px',
          },
          onScroll: (e: React.UIEvent<HTMLDivElement>) => fetchMoreOnBottomReached(e.currentTarget),
        }}
        // Add custom bottom toolbar to show loading state
        renderBottomToolbar={({ table }) => {
          if (!isFetchingNextPage) return null
          return (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                paddingTop: '10px',
                paddingBottom: '5px',
              }}
            >
              <Loader noPadding />
            </Box>
          )
        }}
        muiTableBodyCellSkeletonProps={{
          sx: { display: 'none' }, // Hide all skeleton loaders
        }}
        muiToolbarAlertBannerProps={
          isError
            ? {
                color: 'error',
                children: 'Error loading data',
              }
            : undefined
        }
        initialState={{
          columnVisibility: { country: false },
        }}
        state={{
          //columnFilters,
          globalFilter,
          isLoading: false, // Set to false to prevent built-in loading display
          showAlertBanner: isError,
          showProgressBars: false, // Disable built-in progress bars
          //sorting,
        }}
        rowVirtualizerProps={
          !count || count > 10 || globalFilter.length > 0
            ? {
                //overscan: 4,
                //scrollingDelay: 400,
                initialRect: { width: 0, height: availableHeight },

                //scrollToFn,
                //count: 5000,
                //count,
                /*getItemKey: useCallback(
                  (index) => {
                    console.log(index, flatData[index])
                    return index //flatData[index].id
                  },
                  [flatData],
                ),*/
                estimateSize: () => (isMobile ? 56 : 72),
                overscan: 5,
                // TODO: We could use this to dynamically position "header" inside this number.
                //scrollMargin: 100,
              }
            : {
                initialRect: { width: 0, height: availableHeight },
                // TODO: IS this the bad boy?
                //count: count === 0 ? 1 : count,
                estimateSize: () => (isMobile ? 56 : 72),
                overscan: 5,
              }
        }
        muiTablePaperProps={{
          elevation: 0,
          sx: {
            boxShadow: 'none',
            backgroundColor: 'transparent',
          },
        }}
        muiTableBodyProps={{
          sx: {
            minHeight: isMobile ? 56 : 72,
            '& td': {
              alignItems: 'center',
            },
            '& td > .MuiTypography-root': {
              //fontSize: '10rem',
              color: '#24284e80',
              fontStyle: 'normal',
              paddingTop: '1rem',
              paddingBottom: '1rem',
            },
          },
        }}
        muiTableBodyCellProps={({ row, cell }) => {
          // Safeguard against missing or malformed row data
          if (!row || !row.original) {
            return { sx: {} }
          }

          return {
            sx: {
              fontFamily: 'inherit',
              fontSize: 'inherit',
              borderBottom: `1px solid ${borderGray}`,
              backgroundColor: 'transparent',
              lineHeight: 'normal',
              padding: {
                xs: '10px 3px', // Compact padding on mobile
                md: '16px', // Default padding on larger screens
              },
              minHeight: {
                xs: '56px',
                md: '72px',
              },
              'tr:last-child > &': {
                borderBottomColor: 'transparent',
              },
              ...(row.original.isSeparatorForBelow && {
                borderBottom: `2px solid ${darkGreen} !important`,
              }),
              ...(row.original.isSelf && {
                //border: 'none',
                borderColor: 'transparent',
              }),
              ...(row.original.isBeforeSelf && {
                border: 'none',
              }),
              // Rank column
              ...(cell.column.id === 'rank' && {
                /*width: '40px',
                minWidth: '40px',
                maxWidth: '40px',*/
                width: '50px',
                minWidth: '50px',
                maxWidth: '50px',
                paddingRight: 0,
                justifyContent: 'center',
                flexGrow: 0,
                flexShrink: 0,
                textAlign: isRtl ? 'right' : 'left',
              }),
              // Name column
              ...(cell.column.id === 'name' && {
                minWidth: 0, // Allow shrinking on small screens
                width: 'auto',
                flexGrow: 1,
                flexShrink: 1,
                flexBasis: '100%',
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                '& > span': {
                  width: '100%',
                  display: 'block',
                },
                textAlign: isRtl ? 'right' : 'left',
              }),
              // Result/percentage column
              ...(cell.column.id === 'percentage' && {
                width: '50%',
                minWidth: '50%',
                maxWidth: '50%',
                paddingRight: {
                  xs: !showProgress ? '10px' : '24px',
                  md: !showProgress ? '16px' : '32px',
                },
                justifyContent: 'flex-end',
                flexGrow: 0,
                flexShrink: 0,
                whiteSpace: 'nowrap',
                textAlign: !showProgress ? (!isRtl ? 'right' : 'left') : isRtl ? 'right' : 'left',
                overflow: 'visible',
              }),
            },
          }
        }}
        muiTableBodyRowProps={({ row }) => {
          // Safeguard against missing or malformed row data
          if (!row || !row.original) {
            return { sx: {} }
          }

          return {
            hover: false,
            className: row.original.isSelf ? 'rt-tr--self' : '',
            sx: {
              'tr:first-of-type > &': {
                borderRadiusTop: '8px',
              },
              '&:hover td': {
                backgroundColor: 'transparent !important',
              },
              ...(row.original.isSelf && {
                backgroundColor: lightestBlue,
                borderRadius: '8px',
              }),
              // ...(row.original.isSeparatorForAbove && {
              //   // borderTop: `24px solid ${darkGreen}`,
              //   borderTop: `2px solid ${darkGreen}`,
              // }),
              ...(row.original.isSeparatorForBelow && {
                //borderBottom: `2px solid ${darkGreen}`,
                zIndex: 1,
              }),
            },
          }
        }}
        rowVirtualizerInstanceRef={rowVirtualizerInstanceRef} //get access to the virtualizer instance
        // Hide the entire table head (header row)
        muiTableHeadProps={{
          sx: {
            display: 'none',
          },
        }}
        filterFns={{
          custom: customFilterFn,
        }}
        globalFilterFn="custom"
      />
      {/* Add a custom overlay loader when the main table is loading */}
      {isLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(255, 255, 255, 1)',
            zIndex: 10,
          }}
        >
          <Loader />
        </Box>
      )}
      {membersModal && (
        <LeaderboardMembersModal
          open={!!membersModal}
          onClose={() => setMembersModal(null)}
          goalId={goalId}
          goalDto={goalDto}
          teamId={membersModal.teamId}
          teamName={membersModal.teamName}
          country={membersModal.country}
          token={token}
          showContributions={showGroupMembersContributions}
        />
      )}
    </div>
  )
}

const YumLeaderboardTableReactQueryProvider = (props: IYumLeaderboardTableProps) => {
  //const queryClient = new QueryClient()
  const [queryClient] = useState(() => new QueryClient())
  return (
    <QueryClientProvider client={queryClient}>
      <YumLeaderboardTable {...props} />
      {/* <ReactQueryDevtools initialIsOpen={false} /> */}
    </QueryClientProvider>
  )
}

export default YumLeaderboardTableReactQueryProvider
